import type { CompanyPatchPayload } from "@repo/app-types/entities/company";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { type FC, useState } from "react";
import { useUpdateCompanyMutation } from "../../../api/entities/company.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";

export const CallRecorderSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [updateCompany] = useUpdateCompanyMutation();

  if (!company) {
    return null;
  }

  const currentRecordingSummaryContext =
    company.settings.recording_summary_context || "";

  const [recordingSummaryContext, setRecordingSummaryContext] = useState(
    currentRecordingSummaryContext,
  );

  const onCompanySave = async (updates: CompanyPatchPayload) => {
    try {
      await updateCompany(updates).unwrap();

      showNotification(dispatch, {
        id: `company-updated-${new Date().getTime()}`,
        primaryMessage: `Summary context successfully updated`,
        type: NotificationType.SUCCESS,
      });
    } catch (error) {
      console.error("Failed to update company", error);

      showNotification(dispatch, {
        id: `company-updated-${new Date().getTime()}`,
        primaryMessage: `Failed to update summary context`,
        type: NotificationType.FAILURE,
      });
    }
  };

  const handleSaveSummaryContext = () => {
    onCompanySave({
      settings: { recording_summary_context: recordingSummaryContext },
    });
  };

  const handleCancelSummaryContext = () => {
    setRecordingSummaryContext(currentRecordingSummaryContext);
  };

  return (
    <SettingsSectionPage title={settingsSections.recording.label}>
      <div className="space-y-4">
        <div>
          <div className="space-y-4">
            <Textarea
              value={recordingSummaryContext}
              onChange={setRecordingSummaryContext}
              height="300px"
              placeholder={`# About Acme Solar
We are a residential solar installation company that helps homeowners reduce their energy costs through custom solar panel systems.

# Sales Process
We typically record different kinds of in-person field meetings:
- Initial home consultations and site assessments - First visit to understand the homeowner's energy needs, discuss solar benefits, and conduct preliminary roof evaluation
- Roof inspection and measurement visits - Detailed technical assessment of roof condition, angle, shading, and structural capacity for solar panels
- Solar design presentation meetings - Presenting custom system design, energy production estimates, and financial projections to homeowners
- Contract signing appointments - Final negotiation of terms, financing options, permit discussions, and contract execution
- Post-installation follow-up visits - System performance review, customer satisfaction check, and maintenance education after installation completion

# Key Focus Areas
- Homeowner energy usage and cost concerns
- Roof suitability and installation requirements
- Financing options and incentives
- Timeline and installation process
- Customer objections and concerns

# Additional Notes
Our target customers are homeowners looking to reduce electricity bills and increase home value through solar installations.`}
              label="Recording context"
            />
            {currentRecordingSummaryContext !== recordingSummaryContext && (
              <SaveAndCancelButtons
                onSave={handleSaveSummaryContext}
                onCancel={handleCancelSummaryContext}
              />
            )}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            Context in markdown format about your company and the kinds of
            meetings that will be recorded. More detail helps Alpharun generate
            better summaries.
          </div>
        </div>

        <div>
          <Label size={Size.SMALL} className="mb-2">
            Email summary
          </Label>
          <Checkbox
            isChecked={company.settings.should_send_recording_summary_email}
            onChange={(isChecked) => {
              onCompanySave({
                settings: {
                  should_send_recording_summary_email: isChecked,
                },
              });
            }}
            label="Send email summaries of recorded meetings"
          />
          <div className="mt-2 text-sm text-muted-foreground">
            When enabled, the teammate who recorded the meeting will receive an
            email copy of the recording summary after the meeting.
          </div>
        </div>
      </div>
    </SettingsSectionPage>
  );
};
